import { useContext } from 'react';
import { Instant, PlainDate } from 'temporal-luxon';
import { DateFormat, DateTimeFormat as I18nDateTimeFormat, Locale as I18nLocale } from '@medable/i18n-core-v4';
import { LocaleContext, SetLocaleContext, TimeZoneContext } from '../../../i18n/src';
const toInstant = (date) => date instanceof Date ? Instant.fromEpochMilliseconds(date.getTime()) : Instant.from(date);
/**
 * useDateTimeFormat returns formatters for localized instant, zoned, and plain-date values.
 *
 * Usage:
 * - `formatDateTime(date, { dateOnly, showSeconds })` for viewer time zone (user profile or OS).
 * - `formatDateTimeWithZone(date, { timeZone, dateOnly, showTimeZoneName, showSeconds })` when the time zone is defined by
 *   your data (e.g. stored event time zone).
 * - `formatPlainDate(date)` for ISO local dates (YYYY-MM-DD) without time zone or time.
 *
 * Example:
 * const { formatDateTime, formatDateTimeWithZone, formatPlainDate } = useDateTimeFormat();
 * formatDateTime(new Date(), { showSeconds: true });
 * formatDateTimeWithZone(new Date(), { timeZone: 'America/New_York', showTimeZoneName: 'always' });
 * formatPlainDate('2026-12-30');
 */
export const useDateTimeFormat = () => {
    var _a;
    const locale = useContext(LocaleContext);
    const setLocale = useContext(SetLocaleContext);
    const timeZoneContext = useContext(TimeZoneContext);
    const resolvedLocale = locale.toString();
    const localeInstance = new I18nLocale(resolvedLocale);
    if (!setLocale) {
        throw new Error('DateTimeFormat must be used within an I18nContextProvider.');
    }
    if (localeInstance.isPseudoLocale) {
        return {
            formatDateTime: (_date, options) => `[[[${(options === null || options === void 0 ? void 0 : options.dateOnly) ? 'InstantDate' : 'InstantDateTime'}]]]`,
            formatDateTimeWithZone: (_date, options) => `[[[${(options === null || options === void 0 ? void 0 : options.dateOnly) ? 'ZonedDate' : 'ZonedDateTime'}]]]`,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            formatPlainDate: (_date) => '[[[PlainDate]]]',
        };
    }
    const viewerTimeZone = (_a = timeZoneContext.user) !== null && _a !== void 0 ? _a : timeZoneContext.os;
    return {
        formatDateTime: (date, options) => {
            var _a;
            const dateOnly = (_a = options === null || options === void 0 ? void 0 : options.dateOnly) !== null && _a !== void 0 ? _a : false;
            const showSeconds = (options === null || options === void 0 ? void 0 : options.showSeconds) === 'always' || (options === null || options === void 0 ? void 0 : options.showSeconds) === true;
            const includeTimeZone = viewerTimeZone !== timeZoneContext.os;
            if (!date) {
                return '';
            }
            const timeOptions = !dateOnly && showSeconds ? { second: 'numeric' } : undefined;
            const formatter = dateOnly
                ? new DateFormat(localeInstance, includeTimeZone ? { timeZoneName: 'short' } : undefined)
                : new I18nDateTimeFormat(localeInstance, Object.assign(Object.assign({}, (includeTimeZone && { timeZoneName: 'short' })), timeOptions));
            const instant = toInstant(date);
            const zonedDateTime = instant.toZonedDateTimeISO(viewerTimeZone);
            return formatter.format(zonedDateTime);
        },
        formatDateTimeWithZone: (date, options) => {
            var _a, _b;
            if (!(options === null || options === void 0 ? void 0 : options.timeZone)) {
                throw new Error('DateTimeFormat timeZone is required when variant is "zoned".');
            }
            if (!date) {
                return '';
            }
            const dateOnly = (_a = options === null || options === void 0 ? void 0 : options.dateOnly) !== null && _a !== void 0 ? _a : false;
            const showSeconds = (options === null || options === void 0 ? void 0 : options.showSeconds) === 'always' || (options === null || options === void 0 ? void 0 : options.showSeconds) === true;
            const includeTimeZone = ((_b = options.showTimeZoneName) !== null && _b !== void 0 ? _b : 'auto') === 'always' || options.timeZone !== viewerTimeZone;
            const timeOptions = !dateOnly && showSeconds ? { second: 'numeric' } : undefined;
            const formatter = dateOnly
                ? new DateFormat(localeInstance, includeTimeZone ? { timeZoneName: 'short' } : undefined)
                : new I18nDateTimeFormat(localeInstance, Object.assign(Object.assign({}, (includeTimeZone ? { timeZoneName: 'short' } : undefined)), timeOptions));
            const instant = toInstant(date);
            const zonedDateTime = instant.toZonedDateTimeISO(options.timeZone);
            return formatter.format(zonedDateTime);
        },
        formatPlainDate: (date) => {
            if (!date) {
                return '';
            }
            const formatter = new DateFormat(localeInstance);
            const plainDate = PlainDate.from(date);
            const zonedDateTime = plainDate.toZonedDateTime({ timeZone: viewerTimeZone });
            return formatter.format(zonedDateTime);
        },
    };
};
//# sourceMappingURL=use_date_time_format.js.map